import { useState, useRef } from 'react';

/* Context */
import { useAuthContext } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

/* API */
import { api } from '../../api';

/* Utils */
import { generateInitialAvatar } from '../../utils/avatarUtils';

/* CSS */
import shared from '../../styles/shared.module.css';
import styles from './MyProfile.module.css';

/* Icons */
import edit_icon from '../../assets/icons/edit_icon.svg';
import edit_avatar_clicked from '../../assets/icons/edit_avatar_clicked.svg';
import edit_avatar_unclicked from '../../assets/icons/edit_avatar_unclicked.svg';

/* Components */
import EmailChangeModal from './EmailChangeModal';

// Build a toast message naming exactly which fields were updated.
const buildUpdateMessage = (fields) => {
    if (fields.length === 1) return `Your ${fields[0]} has been updated!`;
    if (fields.length === 2) return `Your ${fields[0]} and ${fields[1]} have been updated!`;
    const last = fields[fields.length - 1];
    return `Your ${fields.slice(0, -1).join(', ')}, and ${last} have been updated!`;
};

const MyProfile = () => {

    const { currentUser, updateUser } = useAuthContext();
    const { showToast } = useToast();

    const [formData, setFormData] = useState({
        username: currentUser?.username || '',
        email: currentUser?.email || '',
        about: currentUser?.about || '',
    });
    const [avatarPreview, setAvatarPreview] = useState(currentUser?.profile_picture_url || generateInitialAvatar(currentUser?.username));
    const [avatarFile, setAvatarFile] = useState(null);

    const [isHoveringAvatar, setIsHoveringAvatar] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [showEmailModal, setShowEmailModal] = useState(false);

    const fileInputRef = useRef(null);
    const hasCustomAvatar = avatarPreview != null && !avatarPreview.startsWith('data:');

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setAvatarPreview(URL.createObjectURL(file));
            setAvatarFile(file);
        }
    };

    const handleSave = async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            showToast("Please enter a valid email address.", 'error');
            return;
        }
        if (!formData.username) {
            showToast("Please enter your username.", 'error');
            return;
        }

        try {
            setIsLoading(true);
            let profile_picture_url;

            if (!hasCustomAvatar) {
                // User removed their avatar (or never had one) — fall back to the initial-letter
                profile_picture_url = generateInitialAvatar(formData.username);
            } else if (avatarFile) {
                // New file selected — upload to S3 first
                const uploadData = new FormData();
                uploadData.append('image', avatarFile);
                const uploadResult = await api.upload('/images', uploadData);
                URL.revokeObjectURL(avatarPreview);
                profile_picture_url = uploadResult.url;
                setAvatarPreview(profile_picture_url);
            } else {
                // Avatar unchanged
                profile_picture_url = currentUser.profile_picture_url;
            }

            // Work out which immediate fields actually changed (email is handled separately)
            const changed = [];
            if (formData.username !== currentUser.username) changed.push('username');
            if ((formData.about || '') !== (currentUser.about || '')) changed.push('bio');
            if ((profile_picture_url ?? null) !== (currentUser.profile_picture_url ?? null)) changed.push('profile picture');

            // Save everything EXCEPT email — email changes are verified separately
            const result = await api.patch('/auth/profile', {
                username: formData.username,
                about: formData.about,
                profile_picture_url,
            });

            updateUser(result.user);
            setAvatarFile(null);

            // Toast naming the fields that saved right now
            if (changed.length > 0) {
                showToast(buildUpdateMessage(changed), 'success');
            }

            // If the email changed, send a code to the new address and confirm via the modal
            if (formData.email !== currentUser.email) {
                await api.post('/auth/request-email-change', { newEmail: formData.email });
                setShowEmailModal(true);
            }

            setIsEditing(false);

        } catch (err) {
            showToast(err.message || "Failed to save profile. Please try again.", 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            {/* --- HEADER: AVATAR --- */}
            <div className={styles.profileHeader}>
                <div className={styles.profileInfo}>
                    {/* The ugly hidden file input */}
                    <input
                        type="file"
                        accept="image/png, image/jpeg"
                        style={{ display: 'none' }}
                        ref={fileInputRef}
                        onChange={handleAvatarChange}
                    />
                    {/* The relative wrapper that holds BOTH the Avatar and the Edit Icon */}
                    <div
                        className={styles.avatarImageWrapper}
                        onMouseEnter={() => isEditing && setIsHoveringAvatar(true)}
                        onMouseLeave={() => isEditing && setIsHoveringAvatar(false)}
                        onClick={() => isEditing && fileInputRef.current.click()}
                        style={{ cursor: isEditing ? 'pointer' : 'default' }}
                    >
                        <img
                            className={styles.profilePic}
                            src={hasCustomAvatar ? avatarPreview : generateInitialAvatar(formData.username)}
                            alt="Profile picture"
                        />
                        <img
                            className={styles.editAvatarIcon}
                            src={isHoveringAvatar ? edit_avatar_clicked : edit_avatar_unclicked}
                            style={{ visibility: isEditing ? 'visible' : 'hidden' }}
                            alt="Edit avatar"
                        />
                    </div>

                    {/* Remove image */}
                    <div
                        className={`${styles.removeImage} ${!hasCustomAvatar ? styles.disabledText : ''}`}
                        style={{ display: isEditing ? 'block' : 'none' }}
                        onClick={() => {
                            // Only allow removing if they actually have a custom image
                            if (hasCustomAvatar) {
                                // Memory cleanup: delete the custom image from memory
                                URL.revokeObjectURL(avatarPreview);
                                // Fall back to the initial-letter avatar
                                setAvatarPreview(generateInitialAvatar(formData.username));
                                setAvatarFile(null);
                                // Clears the file input memory
                                fileInputRef.current.value = "";
                            }
                        }}
                    >
                        Remove profile picture
                    </div>
                </div>
            </div>

            {/* --- PERSONAL INFO SECTION --- */}
            <div>
                <div className={styles.profileSubsectionHeader}>
                    <h3 className={styles.profileSubsectionTitle}>Personal Information</h3>

                    {!isEditing && (
                        <button
                            className={styles.profileSubsectionEdit}
                            onClick={() => setIsEditing(true)}
                        >
                            Edit
                            <img src={edit_icon} alt="Edit" className={styles.profileEditIcon} />
                        </button>
                    )}
                </div>

                <div className={styles.profileForm}>
                    {/* Username */}
                    <div className={styles.formGroup}>
                        <label>Username</label>
                        {isEditing ? (
                            <input
                                type='text'
                                className={styles.settingsInput}
                                value={formData.username}
                                onChange={(e) => handleChange('username', e.target.value)}
                            />
                        ) : (
                            <p className={styles.settingsTextDisplay}>{currentUser?.username}</p>
                        )}
                    </div>

                    {/* Email */}
                    <div className={styles.formGroup}>
                        <label>Email</label>
                        {isEditing ? (
                            <input
                                type='email'
                                className={styles.settingsInput}
                                value={formData.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                            />
                        ) : (
                            <p className={styles.settingsTextDisplay}>{currentUser?.email}</p>
                        )}
                    </div>

                    {/* About */}
                    <div className={styles.formGroup}>
                        <label>About (optional)</label>
                        {isEditing ? (
                            <textarea
                                className={styles.settingsTextarea}
                                value={formData.about}
                                onChange={(e) => handleChange('about', e.target.value)}
                                placeholder='Write something for your bio...'
                            />
                        ) : (
                            currentUser?.about
                                ? <p className={styles.settingsTextDisplay}>{currentUser.about}</p>
                                : <p className={styles.bioPlaceholder}>Write something for your bio...</p>
                        )}
                    </div>

                    {/* Save Button (only visible in Edit Mode) */}
                    {isEditing && (
                        <div className={shared.formActions}>
                            <button
                                className={`${shared.saveSettingsButton} ${isLoading ? shared.buttonLoading : ''}`}
                                onClick={handleSave}
                            >
                                <div className={shared.buttonContent}>
                                    {isLoading ? "Saving..." : "Save"}
                                    {isLoading && <span className={shared.spinner}></span>}
                                </div>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Email change verification modal */}
            <EmailChangeModal
                isOpen={showEmailModal}
                newEmail={formData.email}
                onClose={() => {
                    setShowEmailModal(false);
                    // The change wasn't confirmed — revert the field to the real email
                    setFormData(prev => ({ ...prev, email: currentUser.email }));
                }}
                onConfirmed={(updatedUser) => {
                    updateUser(updatedUser);
                    setShowEmailModal(false);
                    showToast('Your email has been updated!', 'success');
                }}
            />
        </div>
    );
};

export default MyProfile;
